import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SortableLesson from "./SortableLesson";

const sectionTitle = (section) => {
  if (section.title.length > 18) {
    return section.title.slice(0, 18) + "...";
  } else {
    return section.title;
  }
};

const Section = (props) => {
  const {
    sectionId,
    section,
    selectedSection,
    hadnleSelectedSectionChange,
    courseId,
    setLessonFormId,
    setLessonId,
    deleteSection,
  } = props;
  const [lessons, setLessons] = useState(section.lessons || [{ P }]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = lessons.findIndex((item) => item.id === active.id);
      const newIndex = lessons.findIndex((item) => item.id === over.id);
      const array = arrayMove(lessons, oldIndex, newIndex);
      setLessons(array);
      orderLessons(array);
    }
  };

  const orderLessons = async (array) => {
    await fetch(`/courses/${courseId}/sections/${section.id}/lessons_order`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        lesson_ids: array.map((item) => item.id),
      }),
    });
  };

  const deleteLesson = async (lessonId) => {
    const response = await fetch(
      `/sections/${section.id}/lessons/${lessonId}`,
      {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      },
    );
    if (response.ok) {
      setLessons(lessons.filter((lesson) => lesson.id !== lessonId));
      setLessonFormId(null);
    } else {
      // TODO handle error messages
      console.log("ERROR");
    }
  };

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: sectionId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    setLessons(section.lessons || []);
  }, [section.lessons]);

  return (
    <div
      className="flex items-center gap-2 bg-white px-6 py-2 border-b"
      ref={setNodeRef}
      style={style}
    >
      <div className="border bg-gray-50 w-60">
        <div className="flex items-center gap-2 bg-gray-200 px-4 py-2 font-medium">
          <span className="text-lg cursor-move" {...attributes} {...listeners}>
            ≡
          </span>
          <div className="flex justify-between w-full">
            <span
              className="cursor-pointer"
              onClick={() => hadnleSelectedSectionChange(section.id)}
            >
              {sectionTitle(section)}
            </span>
            <button
              className="flex items-center gap-1 text-primaryYellow"
              onClick={() => deleteSection(section.id)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                />
              </svg>
            </button>
          </div>
        </div>

        {selectedSection && selectedSection.id === section.id && (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={lessons.map((lesson) => lesson.id)}
                strategy={verticalListSortingStrategy}
              >
                {lessons.map((lesson) => (
                  <SortableLesson
                    key={lesson.id}
                    lessonId={lesson.id}
                    label={lesson.title}
                    setLessonFormId={setLessonFormId}
                    setLessonId={setLessonId}
                    deleteLesson={deleteLesson}
                    sectionId={section.id}
                  />
                ))}
              </SortableContext>
            </DndContext>

            <div className="px-6 py-3">
              <button
                className="flex items-center gap-1 text-primaryYellow"
                onClick={() => {
                  setLessonFormId(section.id);
                  setLessonId(null);
                }}
              >
                <span className="text-lg">＋</span>
                Dodaj lekcję
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

Section.propTypes = {
  sectionId: PropTypes.string,
  courseId: PropTypes.string,
  setLessonId: PropTypes.func,
  setLessonFormId: PropTypes.func,
  deleteSection: PropTypes.func,
  hadnleSelectedSectionChange: PropTypes.func,
  selectedSection: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    lessons: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
    ),
  }),
  section: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    lessons: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
        lesson_type: PropTypes.string,
        file_url: PropTypes.string,
      }),
    ),
  }),
};

export default Section;
